import React from "react";
import { useParams } from "react-router";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Field, Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import {
  Fieldset,
  SelectField,
  SliderField,
  SwitchField,
  TextField,
} from "@syos-music/form";
import { RatioBox } from "@syos-music/common";
import { MaterialColorSelectField } from "@syos-music/instruments";

import {
  Box,
  Container,
  Fab,
  Grid,
  MenuItem,
  Stack,
  TextField as MuiTextField,
} from "@mui/material";
import {
  OpenInNew as LinkIcon,
  DeleteForever as RemoveIcon,
} from "mdi-material-ui";

import FileField from "components/form/FileField";
import IntlTextField from "components/form/IntlTextField";
import IntlSlugField from "components/form/IntlSlugField";

import Layout from "components/common/Layout";
import SaveFab from "components/common/SaveFab";
import WebSiteLink from "components/common/WebSiteLink";
import MouthpieceFieldDialog from "components/signatureMouthpiece/MouthpieceFieldDialog";

const fragments = {
  signatureMouthpiece: gql`
    fragment SignatureMouthpiecePage_SignatureMouthpiece on SignatureMouthpiece {
      id
      slug {
        en
        fr
      }
      published
      inShop
      markedAsNew
      webJpEnabled

      metadata {
        title {
          en
          fr
        }
        description {
          en
          fr
        }
        image {
          id
          ...FileField_file
        }
      }

      images {
        id
        ...FileField_file
      }

      mouthpieceRef {
        id
        instrument
        ...MouthpieceFieldDialog_MouthpieceRef
      }
      brightness
      power
      mouthpieceColor
      ligatureColor
      designId

      soundShaperComment {
        en
        fr
      }

      artist {
        id
      }
      signature
      artistImage {
        id
        ...FileField_file
      }
      artistQuote {
        en
        fr
        ja
      }
      artistDescription {
        en
        fr
      }
      artistSetup {
        instrument {
          en
          fr
        }
        reed {
          en
          fr
        }
        ligature {
          en
          fr
        }
      }
      videoEmbedUrl
    }

    ${FileField.fragments.file}
    ${MouthpieceFieldDialog.fragments.mouthpieceRef}
  `,
};

const QUERY = gql`
  query ($id: ID!) {
    signatureMouthpiece(id: $id) {
      id

      ...SignatureMouthpiecePage_SignatureMouthpiece
    }

    articles(type: "artist") {
      edges {
        node {
          id
          ... on Article_Artist {
            artistName
          }
        }
      }
    }
  }

  ${fragments.signatureMouthpiece}
`;

const MUTATION = gql`
  mutation (
    $id: ID!
    #
    $artistId: ID
    $slug: IntlStringInput!
    #
    $published: Boolean!
    $inShop: Boolean!
    $markedAsNew: Boolean!
    $webJpEnabled: Boolean!
    #
    $metadata: PageMetadataInput
    #
    $images: [FileInput]
    #
    $mouthpieceRef: MouthpieceRefInput
    $brightness: Float
    $power: Float
    $mouthpieceColor: String
    $ligatureColor: String
    $designId: String
    #
    $soundShaperComment: IntlStringInput
    #
    $signature: String
    $artistImage: FileInput
    $artistQuote: IntlStringInput
    $artistDescription: IntlStringInput
    $artistSetup: SignatureMouthpiece_ArtistSetupInput
    $videoEmbedUrl: String
  ) {
    UpdateSignatureMouthpiece(
      id: $id
      #
      artistId: $artistId
      slug: $slug
      #
      published: $published
      inShop: $inShop
      markedAsNew: $markedAsNew
      webJpEnabled: $webJpEnabled
      #
      metadata: $metadata
      #
      images: $images
      #
      mouthpieceRef: $mouthpieceRef
      brightness: $brightness
      power: $power
      mouthpieceColor: $mouthpieceColor
      ligatureColor: $ligatureColor
      designId: $designId
      #
      soundShaperComment: $soundShaperComment
      #
      signature: $signature
      artistImage: $artistImage
      artistQuote: $artistQuote
      artistDescription: $artistDescription
      artistSetup: $artistSetup
      videoEmbedUrl: $videoEmbedUrl
    ) {
      signatureMouthpiece {
        id

        ...SignatureMouthpiecePage_SignatureMouthpiece
      }
    }
  }
  ${fragments.signatureMouthpiece}
`;

export default function SignatureMouthpiecePage() {
  const { signatureMouthpieceId } = useParams();
  const [updateSignatureMouthpiece] = useMutation(MUTATION, {
    variables: {
      id: signatureMouthpieceId,
    },
  });

  const handleSubmit = React.useCallback(
    (values) => {
      const mouthpieceRef = values.mouthpieceRef
        ? {
            type: values.mouthpieceRef.type,
            clarinetMouthpiece: values.mouthpieceRef.clarinetMouthpiece?.id,
            saxophoneMouthpiece: values.mouthpieceRef.saxophoneMouthpiece?.id,
          }
        : null;

      return updateSignatureMouthpiece({
        variables: {
          ...values,
          mouthpieceRef,
        },
      })
        .then(() => {})
        .catch(() => true);
    },
    [updateSignatureMouthpiece]
  );

  const { data, loading } = useQuery(QUERY, {
    variables: { id: signatureMouthpieceId },
  });

  if (loading) return <Layout.Loading />;

  const instrument = data?.signatureMouthpiece.mouthpieceRef?.instrument;
  const slugPrefix = `/shop/${instrument ?? "[instrument]"}/signature/`;

  const artists =
    data?.articles?.edges
      .map(({ node }) => node)
      .sort((artistA, artistB) => {
        if (artistA.artistName < artistB.artistName) return -1;
        if (artistA.artistName > artistB.artistName) return 1;
        return 0;
      }) ?? [];

  const { id, artist, ...initialValues } = data?.signatureMouthpiece ?? {};

  initialValues.artistId = artist?.id;

  return (
    <Container fixed sx={{ p: 2 }}>
      <Form
        onSubmit={handleSubmit}
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, pristine, submitting, values }) => (
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <Layout.Toolbar fixed>
              <Stack
                spacing={2}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Fab
                  size="small"
                  component={WebSiteLink}
                  locale="fr"
                  to={slugPrefix + data?.signatureMouthpiece.slug?.fr}
                >
                  <LinkIcon />
                </Fab>

                <SaveFab
                  onSave={handleSubmit}
                  disabled={pristine}
                  loading={submitting}
                />
              </Stack>
            </Layout.Toolbar>

            <Stack spacing={2}>
              <Fieldset startCollapsed legend="Infos de la page">
                <Grid container>
                  <Grid item xs={12}>
                    <IntlSlugField
                      name="slug"
                      prefix={slugPrefix}
                      label="URL"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <SwitchField name="published" label="Publié" />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <SwitchField
                      name="inShop"
                      label="Visible dans la boutique"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <SwitchField
                      name="markedAsNew"
                      label="Marqué comme nouveau"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <SwitchField
                      name="webJpEnabled"
                      label="Visible sur syos.jp"
                    />
                  </Grid>
                </Grid>
              </Fieldset>

              <Fieldset startCollapsed legend="Metadonnées" name="metadata">
                <Field
                  name="metadata"
                  isEqual={(a, b) => typeof a === typeof b}
                  component="input"
                  type="hidden"
                />

                <Grid container>
                  <Grid item xs={12}>
                    <IntlTextField
                      name="metadata.title"
                      label="Titre"
                      maxLength={70}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <IntlTextField
                      name="metadata.description"
                      label="Description"
                      multiline
                      minLength={100}
                      maxLength={155}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FileField
                      name="metadata.image"
                      uploadPath={`signature-mouthpieces/${signatureMouthpieceId}`}
                      isPublic
                      preview="image"
                      label="Image de partage"
                      helperText="Facebook, Twitter, …"
                    />
                  </Grid>
                </Grid>
              </Fieldset>

              <Fieldset startCollapsed legend="Carousel">
                <FileField
                  name="images"
                  multiple
                  uploadPath={`signature-mouthpieces/${signatureMouthpieceId}`}
                  isPublic
                  preview="image"
                />
              </Fieldset>

              <Fieldset startCollapsed legend="Bec">
                <Grid container>
                  <Grid item xs={12}>
                    <MouthpieceFieldDialog />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SliderField
                      name="brightness"
                      label="Brillance"
                      min={0}
                      max={10}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SliderField
                      name="power"
                      label="Puisance"
                      min={0}
                      max={10}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MaterialColorSelectField
                      name="mouthpieceColor"
                      allowPrivate
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MaterialColorSelectField
                      name="ligatureColor"
                      allowPrivate
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="designId"
                      label="Design ID"
                      helperText="Solidou"
                    />
                  </Grid>
                </Grid>
              </Fieldset>

              <Fieldset
                startCollapsed
                legend="Commentaire"
                name="soundShaperComment"
              >
                <IntlTextField
                  name="soundShaperComment"
                  label="Commentaire du sound shaper"
                  multiline
                />
              </Fieldset>

              <Fieldset startCollapsed legend="Artiste">
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <SelectField name="artistId" label="Page artiste liée">
                      {artists.map(({ id, artistName }) => (
                        <MenuItem key={id} value={id}>
                          {artistName}
                        </MenuItem>
                      ))}
                    </SelectField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField name="signature" label="Signature" required />
                  </Grid>

                  <Grid item xs={12}>
                    <FileField
                      name="artistImage"
                      uploadPath={`signature-mouthpieces/${signatureMouthpieceId}`}
                      isPublic
                      preview="image"
                      label="Image de résumé"
                      helperText="Visible dans les listes"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <IntlTextField
                      name="artistQuote"
                      label="Citation"
                      multiline
                      locales={
                        values.webJpEnabled ? ["en", "fr", "ja"] : ["en", "fr"]
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <IntlTextField
                      name="artistDescription"
                      label="Description"
                      multiline
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Fieldset
                      legend="Materiel de l’artiste"
                      collapsible={false}
                    >
                      <Grid container>
                        <Grid item xs={12}>
                          <IntlTextField
                            name="artistSetup.instrument"
                            label="Instrument"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IntlTextField
                            name="artistSetup.reed"
                            label="Anche"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IntlTextField
                            name="artistSetup.ligature"
                            label="Ligature"
                          />
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12}>
                    <Field name="videoEmbedUrl">
                      {({ input }) =>
                        input.value ? (
                          <Box
                            sx={{
                              position: "relative",
                              width: "60%",
                              margin: "0 auto",
                            }}
                          >
                            <RatioBox ratio={16 / 9}>
                              <Box
                                component="iframe"
                                title="Video embed"
                                src={input.value}
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                frameBorder="0"
                                sx={{ width: "100%", height: "100%" }}
                              />
                            </RatioBox>

                            <Fab
                              size="small"
                              onClick={() => input.onChange(null)}
                              sx={{
                                position: "absolute",
                                top: "50%",
                                right: 0,
                                transform: "translate(calc(100% + 16px), -50%)",
                              }}
                            >
                              <RemoveIcon />
                            </Fab>
                          </Box>
                        ) : (
                          <MuiTextField
                            onChange={(event) => {
                              const text = event.target.value ?? "";
                              const url = text.match(/src="(.*?)"/i)?.[1];
                              if (url) input.onChange(url);
                            }}
                            label="Video embed code"
                          />
                        )
                      }
                    </Field>
                  </Grid>
                </Grid>
              </Fieldset>
            </Stack>
          </form>
        )}
      </Form>
    </Container>
  );
}
